import React, { forwardRef, useState, useEffect, useId } from 'react';
import classNames from 'classnames';
import { formatNumberWithCommas, parseNumberWithCommas } from '../../utils/numberFormat';

interface InputProps extends React.InputHTMLAttributes<HTMLInputElement> {
  label: string;
  error?: string;
  prefix?: string;
  suffix?: string;
  fullWidth?: boolean;
  formatWithCommas?: boolean;
}

export const Input = forwardRef<HTMLInputElement, InputProps>(
  ({ label, error, prefix, suffix, fullWidth = true, className, formatWithCommas = false, value, onChange, type, id, ...props }, ref) => {
    const [displayValue, setDisplayValue] = useState<string>('');
    const generatedId = useId();
    const inputId = id ?? generatedId;
    const errorId = error ? `${inputId}-error` : undefined;

    useEffect(() => {
      if (formatWithCommas && value !== undefined && value !== null) {
        const isEmpty = value === '' || (typeof value === 'string' && value.trim() === '');
        const numValue = typeof value === 'string' ? parseFloat(value) : value;
        if (isEmpty || (typeof numValue === 'number' && isNaN(numValue))) {
          setDisplayValue('');
        } else if (typeof value === 'number' || (typeof value === 'string' && !isNaN(Number(value)))) {
          setDisplayValue(formatNumberWithCommas(numValue));
        } else {
          setDisplayValue(value.toString());
        }
      } else {
        setDisplayValue(value?.toString() || '');
      }
    }, [value, formatWithCommas]);

    const handleChange = (e: React.ChangeEvent<HTMLInputElement>) => {
      const newValue = e.target.value;
      setDisplayValue(newValue);

      if (onChange) {
        if (formatWithCommas) {
          const parsedValue = parseNumberWithCommas(newValue);
          e.target.value = parsedValue.toString();
        }
        onChange(e);
      }
    };

    return (
      <div className={classNames('mb-2', { 'w-full': fullWidth })}>
        {label && (
          <label
            htmlFor={inputId}
            className="block text-[10px] font-semibold text-ink-muted uppercase tracking-widest mb-1.5"
          >
            {label}
          </label>
        )}
        <div className="relative rounded-lg shadow-sm">
          {prefix && (
            <div className="absolute inset-y-0 left-0 pl-3 flex items-center pointer-events-none">
              <span className="text-ink-faint sm:text-sm">{prefix}</span>
            </div>
          )}
          <input
            id={inputId}
            ref={ref}
            className={classNames(
              'block w-full rounded-lg border bg-white py-2 px-3 text-sm text-ink placeholder:text-ink-faint focus:outline-none focus:ring-2 focus:ring-gold-400 focus:border-gold-400 transition-colors',
              {
                'pl-7': prefix,
                'pr-10': suffix,
                'border-error-300 focus:ring-error-400 focus:border-error-400': error,
                'border-stone-border': !error,
              },
              className
            )}
            value={displayValue}
            onChange={handleChange}
            type={formatWithCommas ? 'text' : type}
            aria-invalid={!!error}
            aria-describedby={errorId}
            {...props}
          />
          {suffix && (
            <div className="absolute inset-y-0 right-0 pr-3 flex items-center pointer-events-none">
              <span className="text-ink-faint sm:text-sm">{suffix}</span>
            </div>
          )}
        </div>
        {error && <p id={errorId} className="mt-1 text-xs text-error-600">{error}</p>}
      </div>
    );
  }
);

Input.displayName = 'Input';